import { useQueryClient } from "@tanstack/react-query";
import { useCallback } from "react";

import { get } from "api";

/**
 * Hook to prefetch Product Detail Page (PDP) data
 * This will pre-load the data into React Query cache so that when
 * the user navigates to the PDP, the data is already available
 */
export const usePrefetchPDP = () => {
  const queryClient = useQueryClient();

  /**
   * Prefetch package/treatment listing PDP data
   * @param listingId - The ID of the listing to prefetch
   */
  const prefetchPackagePDP = useCallback(
    (listingId: string) => {
      // Main listing data
      queryClient.prefetchQuery(["/listings/{id}", { id: listingId }], () =>
        get("/listings/{id}", { id: listingId })
      );

      // Related data that's shown on the PDP
      queryClient.prefetchQuery(
        ["/listings/{id}/expectations", { id: listingId }],
        () => get("/listings/{id}/expectations", { id: listingId })
      );

      queryClient.prefetchQuery(
        ["/listings/{id}/before-afters", { id: listingId }],
        () => get("/listings/{id}/before-afters", { id: listingId })
      );

      queryClient.prefetchQuery(
        ["/listings/{id}/media", { id: listingId }],
        () => get("/listings/{id}/media", { id: listingId })
      );

      queryClient.prefetchQuery(
        ["/listings/{id}/packages", { id: listingId }],
        () => get("/listings/{id}/packages", { id: listingId })
      );

      queryClient.prefetchQuery(
        ["/listings/{id}/related", { id: listingId }],
        () => get("/listings/{id}/related", { id: listingId })
      );

      queryClient.prefetchQuery(
        ["/shop/discounts/listings/{listingId}", { listingId }],
        () => get("/shop/discounts/listings/{listingId}", { listingId }),
        { staleTime: 1000 * 60 * 5 }
      );
    },
    [queryClient]
  );

  /**
   * Prefetch membership PDP data
   * @param membershipId - The ID of the membership to prefetch
   */
  const prefetchMembershipPDP = useCallback(
    (membershipId: string) => {
      queryClient.prefetchQuery(["/membership", { membershipId }], () =>
        get("/memberships/{membershipId}", { membershipId })
      );

      queryClient.prefetchQuery(
        ["/membership/{membershipId}/rewards", { membershipId }],
        () => get("/memberships/{membershipId}/rewards", { membershipId })
      );
    },
    [queryClient]
  );

  /**
   * Prefetch retail product PDP data
   * @param productId - The ID of the retail product to prefetch
   */
  const prefetchProductPDP = useCallback(
    (productId: string) => {
      queryClient.prefetchQuery(
        [
          "/retailproducts/{retailProductListingId}/shop-product-listing-details",
          { retailProductListingId: productId },
        ],
        () =>
          get(
            "/retailproducts/{retailProductListingId}/shop-product-listing-details",
            { retailProductListingId: productId }
          )
      );
    },
    [queryClient]
  );

  /**
   * Prefetch gift cards list
   * Gift cards don't have individual PDPs, they share a common listing page
   */
  const prefetchGiftCards = useCallback(() => {
    queryClient.prefetchQuery(["/gift-cards"], () => get("/gift-cards", {}));
  }, [queryClient]);

  return {
    prefetchPackagePDP,
    prefetchMembershipPDP,
    prefetchProductPDP,
    prefetchGiftCards,
  };
};
